'use client';

import React, { useState } from 'react';
import { useRouter } from 'next/navigation';
import Header from '@/components/common/Header';
import { supportRequestService } from '@/services/supportRequestService';
import { SupportRequestFormData } from '@/types/support-request.types';

interface FormErrors {
  full_name?: string;
  phone?: string;
  email?: string;
  issue_description?: string;
}

export default function ServiceSupportRequestForm() {
  const router = useRouter();
  const [formData, setFormData] = useState<SupportRequestFormData>({
    full_name: '',
    company: '',
    phone: '',
    email: '',
    issue_description: ''
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [charCount, setCharCount] = useState(0);

  const maxChars = 1000; // Approximately 2 paragraphs

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Name validation
    if (!formData.full_name?.trim()) {
      newErrors.full_name = 'Name is required';
    } else if (formData.full_name.trim().length < 2) {
      newErrors.full_name = 'Name must be at least 2 characters';
    }

    // Phone validation
    const phoneRegex = /^[\d\s\-\(\)\+]{10,}$/;
    if (!formData.phone?.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!phoneRegex.test(formData.phone.replace(/\s/g, ''))) {
      newErrors.phone = 'Please enter a valid phone number';
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email?.trim()) {
      newErrors.email = 'Email is required';
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Issue description validation
    if (!formData.issue_description?.trim()) {
      newErrors.issue_description = 'Issue description is required';
    } else if (formData.issue_description.trim().length < 20) {
      newErrors.issue_description = 'Please provide at least 20 characters of description';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    
    if (name === 'issue_description') {
      if (value.length <= maxChars) {
        setFormData(prev => ({ ...prev, [name]: value }));
        setCharCount(value.length);
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }

    // Clear error for this field when user starts typing
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const result = await supportRequestService.submitSupportRequest(formData);
      
      if (result.success) {
        setSubmitSuccess(true);
        // Reset form
        setFormData({
          full_name: '',
          company: '',
          phone: '',
          email: '',
          issue_description: ''
        });
        setCharCount(0);
        
        // Show success message for 3 seconds then redirect
        setTimeout(() => {
          router.push('/resources');
        }, 3000);
      }
    } catch (error) {
      console.error('Submit error:', error);
      setSubmitError(
        error instanceof Error 
          ? error.message 
          : 'Failed to submit support request. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitSuccess) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="pt-32 pb-24 px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mx-auto text-center">
            <div className="bg-green-50 border-2 border-green-500 rounded-lg p-8">
              <div className="text-6xl mb-4">✓</div>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Request Submitted Successfully
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                Thank you for contacting us. Our support team will review your request and get back to you shortly.
              </p>
              <p className="text-sm text-gray-500">
                Redirecting to resources page...
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
              Service & Support Request
            </h1>
            <p className="text-lg sm:text-xl text-gray-600">
              Need assistance? Submit your request and our team will help identify issues, locate the correct parts, or guide you through the next steps.
            </p>
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="pb-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <form onSubmit={handleSubmit} className="space-y-8">
            {submitError && (
              <div className="bg-red-50 border-2 border-red-500 rounded-lg p-4">
                <p className="text-red-800 font-semibold">{submitError}</p>
              </div>
            )}

            {/* Contact Information Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Name Field */}
              <div>
                <label htmlFor="full_name" className="block text-sm font-semibold text-gray-900 mb-2">
                  Name *
                </label>
                <input
                  type="text"
                  id="full_name"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-0 transition-colors ${
                    errors.full_name 
                      ? 'border-red-500 focus:border-red-500' :'border-gray-200 focus:border-gray-900'
                  }`}
                  placeholder="John Doe"
                />
                {errors.full_name && (
                  <p className="mt-2 text-sm text-red-600">{errors.full_name}</p>
                )}
              </div>

              {/* Company Field */}
              <div>
                <label htmlFor="company" className="block text-sm font-semibold text-gray-900 mb-2">
                  Company
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-0 focus:border-gray-900 transition-colors"
                  placeholder="Your Company (Optional)"
                />
              </div>

              {/* Phone Field */}
              <div>
                <label htmlFor="phone" className="block text-sm font-semibold text-gray-900 mb-2">
                  Phone *
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-0 transition-colors ${
                    errors.phone 
                      ? 'border-red-500 focus:border-red-500' :'border-gray-200 focus:border-gray-900'
                  }`}
                  placeholder="+1 (555) 123-4567"
                />
                {errors.phone && (
                  <p className="mt-2 text-sm text-red-600">{errors.phone}</p>
                )}
              </div>

              {/* Email Field */}
              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-gray-900 mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-0 transition-colors ${
                    errors.email 
                      ? 'border-red-500 focus:border-red-500' :'border-gray-200 focus:border-gray-900'
                  }`}
                  placeholder="john@example.com"
                />
                {errors.email && (
                  <p className="mt-2 text-sm text-red-600">{errors.email}</p>
                )}
              </div>
            </div>

            {/* Issue Description */}
            <div>
              <label htmlFor="issue_description" className="block text-sm font-semibold text-gray-900 mb-2">
                Issue Description *
              </label>
              <textarea
                id="issue_description"
                name="issue_description"
                value={formData.issue_description}
                onChange={handleInputChange}
                rows={8}
                className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-0 transition-colors resize-y ${
                  errors.issue_description 
                    ? 'border-red-500 focus:border-red-500' :'border-gray-200 focus:border-gray-900'
                }`}
                placeholder="Please describe your issue or support request in detail. Include any relevant information such as error messages, part numbers, or specific circumstances..."
              />
              <div className="flex justify-between items-center mt-2">
                {errors.issue_description ? (
                  <p className="text-sm text-red-600">{errors.issue_description}</p>
                ) : (
                  <p className="text-sm text-gray-500">
                    Provide detailed information about your issue
                  </p>
                )}
                <p className={`text-sm ${charCount > maxChars * 0.9 ? 'text-red-600' : 'text-gray-500'}`}>
                  {charCount} / {maxChars}
                </p>
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex justify-end space-x-4">
              <button
                type="button"
                onClick={() => router.push('/resources')}
                className="px-8 py-3 border-2 border-gray-900 text-gray-900 rounded-lg font-semibold hover:bg-gray-50 transition-colors duration-300"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className={`px-8 py-3 bg-gray-900 text-white rounded-lg font-semibold hover:bg-gray-800 transition-colors duration-300 ${
                  isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Request'}
              </button>
            </div>

            {/* Required Fields Note */}
            <p className="text-sm text-gray-500 text-center">
              * Required fields
            </p>
          </form>
        </div>
      </section>
    </div>
  );
}